// src/services/audienceApi.ts
import api from './api';
import { 
  EngagementDashboard, 
  AssetEngagementSummary, 
  DevicePerformance,
  HourlyEngagement,
  AudienceInsight 
} from '../types/audience';

export const audienceApi = {
  // Get engagement dashboard data
  getEngagementDashboard: async (days: number = 7): Promise<EngagementDashboard> => {
    const response = await api.get(`/viewerengagement/dashboard/${days}`);
    
    // Transform the response to match our frontend types
    return {
      dateRange: {
        start: new Date(response.data.dateRange.start),
        end: new Date(response.data.dateRange.end)
      },
      totalViewers: response.data.totalViewers,
      totalEngagementSeconds: response.data.totalEngagementSeconds,
      dailyTrends: response.data.dailyTrends.map((trend: any) => ({
        date: new Date(trend.date),
        totalViewers: trend.totalViewers,
        totalEngagementSeconds: trend.totalEngagementSeconds
      })),
      topAssets: response.data.topAssets
    };
  },

  // Get asset engagement details
  getAssetEngagement: async (assetId: number): Promise<AssetEngagementSummary> => {
    const response = await api.get(`/viewerengagement/asset/${assetId}`);
    return response.data;
  },

  // Get device performance data (derived from engagement summaries)
  getDevicePerformance: async (days: number = 7): Promise<DevicePerformance[]> => {
    try {
      // Call the real API endpoint for device performance
      const response = await api.get(`/viewerengagement/devices/${days}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch device performance:', error);
      
      // On error, return devices with zero engagement (no fake data)
      try {
        const devicesResponse = await api.get('/devices');
        const devices = devicesResponse.data;
        
        return devices.map((device: any) => ({
          deviceId: device.deviceId,
          deviceName: device.deviceName,
          location: device.location || 'Unknown Location',
          totalViewers: 0,
          averageAttentionScore: 0,
          viewCount: 0
        }));
      } catch (deviceError) {
        console.error('Failed to fetch devices:', deviceError);
        return [];
      }
    }
  },

  // Get hourly engagement heatmap data
  getHourlyEngagement: async (days: number = 7): Promise<HourlyEngagement[]> => {
    try {
      const response = await api.get(`/viewerengagement/hourly/${days}`);
      return response.data.map((item: any) => ({
        hour: item.hour,
        dayOfWeek: item.dayOfWeek,
        engagementLevel: item.engagementLevel
      }));
    } catch (error) {
      console.error('Failed to fetch hourly engagement:', error);
      return [];
    }
  },

  // Get AI-powered insights
  getAudienceInsights: async (days: number = 7): Promise<AudienceInsight[]> => {
    try {
      const response = await api.get(`/viewerengagement/insights/${days}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch audience insights:', error);
      return [];
    }
  },

  // Export engagement report
  exportEngagementReport: async (days: number = 7, format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> => {
    const response = await api.get(`/viewerengagement/export/${days}?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};
